import { TFunction } from 'i18next';

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    : `${m}:${s.toString().padStart(2, '0')}`;
};

export const formatStreamingDuration = (milliseconds: number, t: TFunction, locale: string): string => {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  
  // For RTL languages (Hebrew and Yiddish), we want the numbers to be formatted in their script
  const formatNumber = (num: number): string => {
    if (locale === 'he' || locale === 'yi') {
      return new Intl.NumberFormat(locale).format(num);
    }
    return num.toString();
  };

  const isRTL = locale === 'he' || locale === 'yi';
  
  // Handle case when duration is less than a minute
  if (minutes === 0) {
    if (isRTL) {
      return `${t('time.minuteShort')}${formatNumber(1)}`;
    }
    return `${formatNumber(1)}${t('time.minuteShort')}`;
  }

  if (hours > 0) {
    // For RTL languages, we want the time unit before the number
    if (isRTL) {
      return `${t('time.hourShort')}${formatNumber(hours)} ${t('time.minuteShort')}${formatNumber(minutes)}`;
    }
    return `${formatNumber(hours)}${t('time.hourShort')} ${formatNumber(minutes)}${t('time.minuteShort')}`;
  }
  
  // For RTL languages, we want the time unit before the number
  if (isRTL) {
    return `${t('time.minuteShort')}${formatNumber(minutes)}`;
  }
  return `${formatNumber(minutes)}${t('time.minuteShort')}`;
};

export const formatRelativeTime = (date: Date | string, locale: string = 'en'): string => {
  try {
    const now = new Date();
    const then = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    // Manual translations for Yiddish relative times
    if (locale === 'yi') {
      if (diffInSeconds < 60) return `${Math.floor(diffInSeconds)} סעקונדעס צוריק`;
      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return minutes === 1 ? 'איין מינוט צוריק' : `${minutes} מינוט צוריק`;
      }
      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return hours === 1 ? 'איין שעה צוריק' : `${hours} שעה צוריק`;
      }
      if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return days === 1 ? 'איין טאָג צוריק' : `${days} טעג צוריק`;
      }
      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return months === 1 ? 'איין חודש צוריק' : `${months} חדשים צוריק`;
      }
      const years = Math.floor(diffInSeconds / 31536000);
      return years === 1 ? 'איין יאָר צוריק' : `${years} יאָר צוריק`;
    }
    
    // Manual translations for Hebrew relative times
    if (locale === 'he') {
      if (diffInSeconds < 60) return `לפני ${Math.floor(diffInSeconds)} שניות`;
      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return minutes === 1 ? 'לפני דקה' : `לפני ${minutes} דקות`;
      }
      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return hours === 1 ? 'לפני שעה' : `לפני ${hours} שעות`;
      }
      if (diffInSeconds < 2592000) {
        const days = Math.floor(diffInSeconds / 86400);
        return days === 1 ? 'לפני יום' : `לפני ${days} ימים`;
      }
      if (diffInSeconds < 31536000) {
        const months = Math.floor(diffInSeconds / 2592000);
        return months === 1 ? 'לפני חודש' : `לפני ${months} חודשים`;
      }
      const years = Math.floor(diffInSeconds / 31536000);
      return years === 1 ? 'לפני שנה' : `לפני ${years} שנים`;
    }

    // For other languages or as a fallback, try to use Intl.RelativeTimeFormat
    try {
      // Check if Intl.RelativeTimeFormat is supported
      if (typeof Intl === 'undefined' || !Intl.RelativeTimeFormat) {
        throw new Error('Intl.RelativeTimeFormat not supported');
      }
      
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      
      if (diffInSeconds < 60) {
        return rtf.format(-Math.floor(diffInSeconds), 'second');
      }
      if (diffInSeconds < 3600) {
        return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
      }
      if (diffInSeconds < 86400) {
        return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
      }
      if (diffInSeconds < 2592000) {
        return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
      }
      if (diffInSeconds < 31536000) {
        return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
      }
      return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
    } catch (e) {
      // If there's an error with the specific language, try with just the language code
      try {
        const languageCode = locale.split('-')[0];
        const rtf = new Intl.RelativeTimeFormat(languageCode, { numeric: 'auto' });
        
        if (diffInSeconds < 60) return rtf.format(-Math.floor(diffInSeconds), 'second');
        if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
        if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
        if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
        if (diffInSeconds < 31536000) return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
        return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
      } catch (innerError) {
        // English fallback if everything else fails
        if (diffInSeconds < 60) return `${Math.floor(diffInSeconds)} seconds ago`;
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
        if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
        return `${Math.floor(diffInSeconds / 31536000)} years ago`;
      }
    }
  } catch (error) {
    // Final fallback if everything else fails
    console.error('Error formatting relative time:', error);
    return new Date(date).toLocaleDateString(locale);
  }
};

export const formatDate = (date: Date | string, locale: string = 'en'): string => {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    calendar: locale === 'he' || locale === 'yi' ? 'hebrew' : 'gregory'
  }).format(new Date(date));
}; 